import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import SettingsSelect from './SettingsSelect';
import { CalendarClock, Package, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import {
  ASSET_FORM_STYLES,
  CategoryChip,
  unwrapData,
} from '../Accounting/Assets/assetUtils';

/**
 * Inställningar → Tillgångar.
 *
 *   1. Automation card — the tenant-wide switch for the automatic monthly
 *      depreciation run (GET/PUT /:companyId/assets/settings).
 *   2. Categories card — the seeded asset categories with their default
 *      nyttjandeperiod and the five accounts each category posts to.
 *      Per-row edit with kontoplan-backed account pickers (the accounts
 *      listing, GET /accounting/accounts?scope=company);
 *      saves via PUT /:companyId/assets/categories/:categoryId and renders
 *      backend validation errors inline.
 */

// The five editable account roles, in posting order.
const ACCOUNT_FIELDS = [
  { key: 'asset_account', label: 'Tillgångskonto' },
  { key: 'accdep_account', label: 'Ack. avskrivningar' },
  { key: 'depexp_account', label: 'Avskrivningskostnad' },
  { key: 'gain_account', label: 'Vinst vid försäljning' },
  { key: 'loss_account', label: 'Förlust vid försäljning' },
];

const formatFireTime = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleString('sv-SE', {
    timeZone: 'Europe/Stockholm', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatPeriod = (period) => {
  const [year, month] = String(period || '').split('-').map(Number);
  if (!year || !month) return '—';
  return new Date(year, month - 1, 1).toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });
};

// The schedule's last run, in the user's words. `succeeded` means the wake-up
// was handled; what it booked is in Tillgångar → Körningar.
const lastRunText = (run) => {
  if (!run) return null;
  const when = formatFireTime(run.finishedAt || run.scheduledFor);
  if (run.status === 'succeeded') return `Senaste körning: ${when} — genomförd.`;
  if (run.status === 'failed') return `Senaste körning: ${when} — misslyckades${run.reason ? ` (${run.reason})` : ''}.`;
  if (run.status === 'skipped') return `Senaste körning: ${when} — hoppades över.`;
  // running: the attempt is in progress, not failed. pending with attempts: waiting for the next one.
  if (run.status === 'running') return 'En körning pågår.';
  if (run.attempt > 0) return `Senaste körning misslyckades vid försök ${run.attempt} av 5 — ett nytt försök görs automatiskt.`;
  return 'En körning väntar på att starta.';
};

const monthsToYearsLabel = (months) => {
  const m = parseInt(months, 10);
  if (!m) return '—';
  return m % 12 === 0 ? `${m / 12} år` : `${m} mån`;
};

// Inline editor for one category row. `accounts` are react-select options
// keyed on account_number (the PUT contract takes numbers, not ids).
const CategoryEditor = ({ category, accounts, onCancel, onSaved, companyId }) => {
  const [form, setForm] = useState(() => ({
    name_sv: category.name_sv || '',
    default_useful_months: String(category.default_useful_months ?? ''),
    asset_account: category.asset_account || '',
    accdep_account: category.accdep_account || '',
    depexp_account: category.depexp_account || '',
    gain_account: category.gain_account || '',
    loss_account: category.loss_account || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const months = parseInt(form.default_useful_months, 10);
  const monthsValid = months >= 12 && months <= 600;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await axios.put(
        getApiUrl(`${companyId}/assets/categories/${category.category_id}`),
        {
          name_sv: form.name_sv.trim(),
          default_useful_months: months,
          asset_account: form.asset_account,
          accdep_account: form.accdep_account,
          depexp_account: form.depexp_account,
          gain_account: form.gain_account,
          loss_account: form.loss_account,
        },
        { withCredentials: true }
      );
      const data = unwrapData(response);
      toast.success('Kategorin är uppdaterad.');
      onSaved(Array.isArray(data) ? data : data?.categories);
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte spara kategorin. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const renderAccountPicker = (field) => {
    return (
      <div key={field.key} style={{ flex: '1 1 220px', minWidth: 200 }}>
        <div className="asset-settings-field-label">{field.label}</div>
        <SettingsSelect
          options={accounts}
          value={form[field.key] || null}
          onChange={(value) => setField(field.key, value || '')}
          placeholder={form[field.key] ? `${form[field.key]} (saknas i kontoplanen)` : 'Välj konto'}
          aria-label={field.label}
        />
      </div>
    );
  };

  return (
    <div className="asset-settings-editor">
      <div className="d-flex flex-wrap gap-3 mb-3">
        <div style={{ flex: '2 1 260px', minWidth: 220 }}>
          <div className="asset-settings-field-label">Namn</div>
          <input
            type="text"
            className="modern-input"
            value={form.name_sv}
            onChange={(e) => setField('name_sv', e.target.value)}
            maxLength={100}
          />
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 150 }}>
          <div className="asset-settings-field-label">Nyttjandeperiod (månader)</div>
          <input
            type="number"
            className="modern-input"
            min="12"
            max="600"
            step="12"
            value={form.default_useful_months}
            onChange={(e) => setField('default_useful_months', e.target.value)}
          />
          <div className="asset-settings-field-hint">
            {monthsValid ? `= ${monthsToYearsLabel(months)}` : 'Ange 12–600 månader.'}
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3">
        {ACCOUNT_FIELDS.map(renderAccountPicker)}
      </div>

      {error && (
        <Alert variant="danger" className="py-2 mt-3 mb-0" style={{ fontSize: '0.8125rem' }}>
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button variant="outline-secondary" size="sm" onClick={onCancel} disabled={saving}>
          Avbryt
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={saving || !form.name_sv.trim() || !monthsValid}
        >
          {saving ? 'Sparar…' : 'Spara kategori'}
        </Button>
      </div>
    </div>
  );
};

const AssetSettings = ({ companyId }) => {
  // Automation card state.
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  // Categories card state.
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(getApiUrl(`${companyId}/assets/settings`), { withCredentials: true });
        if (!cancelled) setSettings(unwrapData(response) || {});
      } catch (err) {
        if (!cancelled) setSettingsError('Kunde inte hämta inställningarna. Försök igen senare.');
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const response = await axios.get(getApiUrl(`${companyId}/assets/categories`), { withCredentials: true });
      const data = unwrapData(response);
      const rows = Array.isArray(data) ? data : data?.categories;
      setCategories(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setCategoriesError('Kunde inte hämta kategorierna. Försök igen senare.');
    } finally {
      setCategoriesLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Kontoplan for the account pickers — the accounts listing, keyed on
  // account_number since the category PUT contract stores numbers.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await axios.get(
          getApiUrl(`${companyId}/accounting/accounts?scope=company`),
          { withCredentials: true }
        );
        if (cancelled) return;
        const options = (result.data?.accounts || []).map((account) => ({
          value: account.account_number,
          label: `${account.account_number} - ${account.account_name}`,
        }));
        setAccounts(options);
      } catch (err) {
        // Pickers degrade to showing the raw numbers; row edit still opens.
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const automationEnabled = Boolean(settings?.automationEnabled);
  const activeAssetCount = settings?.activeAssetCount;

  const saveSettings = async (patch, successMessage) => {
    setSettingsSaving(true);
    try {
      const response = await axios.put(
        getApiUrl(`${companyId}/assets/settings`),
        {
          automationEnabled: settings?.automationEnabled ?? true,
          cadence: settings?.cadence || 'monthly',
          ...patch,
        },
        { withCredentials: true }
      );
      setSettings(unwrapData(response) || { ...settings, ...patch });
      toast.success(successMessage);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte spara inställningen.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleToggleAutomation = (next) => saveSettings(
    { automationEnabled: next },
    next ? 'Automatiska avskrivningar är på.' : 'Automatiska avskrivningar är avstängda.'
  );

  const handleCadenceChange = (next) => saveSettings(
    { cadence: next },
    next === 'yearly'
      ? 'Avskrivningar bokförs årsvis — hela året i ett verifikat i räkenskapsårets sista månad.'
      : 'Avskrivningar bokförs månadsvis.'
  );

  const handleCategorySaved = (updatedRows) => {
    setEditingId(null);
    if (Array.isArray(updatedRows)) setCategories(updatedRows);
    else fetchCategories();
  };

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => (a.category_id || 0) - (b.category_id || 0)),
    [categories]
  );

  return (
    <div className="asset-form">
      {/* 1 ── Automation */}
      <div className="modern-card">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <CalendarClock size={16} /> Avskrivningar
            </h2>
            <p className="section-subtitle">
              Styr hur LedgerLoop bokför de planenliga avskrivningarna för företagets tillgångar.
            </p>
          </div>
        </div>
        <div className="modern-card-body">
          {settingsLoading ? (
            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
          ) : settingsError ? (
            <Alert variant="warning" className="mb-0" style={{ fontSize: 13 }}>{settingsError}</Alert>
          ) : (
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.9375rem' }}>
                  Automatiska månadsavskrivningar
                </div>
                <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 2 }}>
                  Körs automatiskt den 1:a varje månad och bokför avskrivningarna per den sista dagen i perioden.
                </div>
                {activeAssetCount !== undefined && activeAssetCount !== null && (
                  <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Package size={14} />
                    {activeAssetCount === 1
                      ? '1 aktiv tillgång omfattas.'
                      : `${activeAssetCount} aktiva tillgångar omfattas.`}
                  </div>
                )}
              </div>
              <Form.Check
                type="switch"
                id="asset-automation-switch"
                checked={automationEnabled}
                disabled={settingsSaving}
                onChange={(e) => handleToggleAutomation(e.target.checked)}
                aria-label="Automatiska avskrivningar"
                style={{ transform: 'scale(1.25)', transformOrigin: 'right top' }}
              />
            </div>
          )}

          {!settingsLoading && !settingsError && automationEnabled && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.9375rem', marginBottom: 6 }}>
                Avskrivningsfrekvens
              </div>
              <Form.Check
                type="radio"
                id="asset-cadence-monthly"
                name="asset-cadence"
                label="Månadsvis — varje månad bokförs per månadens sista dag, ger ett rättvisande resultat varje månad"
                checked={(settings?.cadence || 'monthly') === 'monthly'}
                disabled={settingsSaving}
                onChange={() => handleCadenceChange('monthly')}
                style={{ fontSize: '0.875rem', marginBottom: 6 }}
              />
              <Form.Check
                type="radio"
                id="asset-cadence-yearly"
                name="asset-cadence"
                label="Årsvis — hela årets avskrivningar i ett verifikat i räkenskapsårets sista månad"
                checked={settings?.cadence === 'yearly'}
                disabled={settingsSaving}
                onChange={() => handleCadenceChange('yearly')}
                style={{ fontSize: '0.875rem' }}
              />
              {/* The schedule behind the switch: next run, last result, sync state. */}
              <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 12 }}>
                {settings?.schedule?.state === 'active' && settings?.cadence === 'yearly' && (
                  <div>
                    Nästa bokföring: {settings.nextPostingPeriod ? `räkenskapsårets sista månad, ${formatPeriod(settings.nextPostingPeriod)}` : '—'}.
                  </div>
                )}
                {settings?.schedule?.state === 'active' && settings?.cadence !== 'yearly' && (
                  <div>
                    Nästa körning: {formatFireTime(settings.schedule.nextFireAt)}
                    {settings.nextPostingPeriod ? ` — bokför ${formatPeriod(settings.nextPostingPeriod)}` : ''}.
                  </div>
                )}
                {settings?.schedule?.state === 'paused' && (
                  <div>Ingen körning är planerad{activeAssetCount === 0 ? ' — det finns inga aktiva tillgångar' : ''}.</div>
                )}
                {lastRunText(settings?.schedule?.lastRun) && (
                  <div
                    style={{
                      marginTop: 2,
                      color: settings.schedule.lastRun.status === 'failed' ? '#b91c1c' : '#6b7280',
                    }}
                  >
                    {lastRunText(settings.schedule.lastRun)}
                  </div>
                )}
                {(settings?.scheduleSynced === false || settings?.scheduleAvailable === false) && (
                  <div style={{ marginTop: 2 }}>Schemat uppdateras inom kort.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 2 ── Categories */}
      <div className="modern-card">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <Package size={16} /> Kategorier & konton
            </h2>
            <p className="section-subtitle">
              Varje kategori bestämmer standard-nyttjandeperiod och vilka konton
              avskrivningar, försäljningar och utrangeringar bokförs på.
            </p>
          </div>
        </div>
        <div className="modern-card-body" style={{ padding: 0 }}>
          {categoriesLoading ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
          ) : categoriesError ? (
            <div style={{ padding: 16 }}>
              <Alert variant="warning" className="mb-0" style={{ fontSize: 13 }}>{categoriesError}</Alert>
            </div>
          ) : sortedCategories.length === 0 ? (
            <p className="text-muted mb-0" style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem' }}>
              Inga kategorier hittades.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table className="mb-0 align-middle asset-settings-table">
                <thead>
                  <tr>
                    <th style={{ paddingLeft: 24 }}>Kategori</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Nyttjandeperiod</th>
                    <th>Konton</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {sortedCategories.map((category) => {
                    const isEditing = editingId === category.category_id;
                    // Compact labeled pairs — wraps vertically inside the cell
                    // instead of ever forcing horizontal scroll.
                    const accountPairs = [
                      { label: 'Tillgång', value: category.asset_account },
                      { label: 'Ack. avskr.', value: category.accdep_account },
                      { label: 'Kostnad', value: category.depexp_account },
                      {
                        label: 'Vinst/Förlust',
                        value: [category.gain_account, category.loss_account].filter(Boolean).join(' / '),
                      },
                    ];
                    return (
                      <React.Fragment key={category.category_id}>
                        <tr>
                          <td style={{ paddingLeft: 24 }}>
                            <span className="d-inline-flex align-items-center gap-2">
                              <CategoryChip code={category.code} size={30} iconSize={15} />
                              <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}>
                                {category.name_sv}
                              </span>
                            </span>
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {monthsToYearsLabel(category.default_useful_months)}
                          </td>
                          <td>
                            <span className="asset-settings-accounts">
                              {accountPairs.map((pair) => (
                                <span key={pair.label} className="asset-settings-account-pair">
                                  <span className="asset-settings-account-label">{pair.label}</span>
                                  <span className="asset-settings-account-value">{pair.value || '—'}</span>
                                </span>
                              ))}
                            </span>
                          </td>
                          <td className="text-end" style={{ paddingRight: 16 }}>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-1"
                              style={{ color: isEditing ? '#6b7280' : 'var(--primary)' }}
                              title={isEditing ? 'Stäng' : 'Redigera'}
                              aria-label={isEditing ? `Stäng redigering av ${category.name_sv}` : `Redigera ${category.name_sv}`}
                              onClick={() => setEditingId(isEditing ? null : category.category_id)}
                            >
                              {isEditing ? <X size={15} /> : <Pencil size={15} />}
                            </Button>
                          </td>
                        </tr>
                        {isEditing && (
                          <tr className="asset-settings-editor-row">
                            <td colSpan={4} style={{ padding: 0 }}>
                              <CategoryEditor
                                category={category}
                                accounts={accounts}
                                companyId={companyId}
                                onCancel={() => setEditingId(null)}
                                onSaved={handleCategorySaved}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <style>{ASSET_FORM_STYLES}</style>
      <style>{`
        .asset-settings-table thead th {
          background: transparent;
          color: #6b7280;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }
        .asset-settings-table tbody td { border-color: #f3f4f6; }
        .asset-settings-accounts {
          display: flex;
          flex-wrap: wrap;
          gap: 4px 18px;
        }
        .asset-settings-account-pair { display: inline-flex; flex-direction: column; }
        .asset-settings-account-label {
          font-size: 0.6563rem;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          line-height: 1.2;
        }
        .asset-settings-account-value {
          font-size: 0.8125rem;
          color: #374151;
          font-variant-numeric: tabular-nums;
          line-height: 1.3;
        }
        .asset-settings-editor-row > td { background: #f9fafb; }
        .asset-settings-editor { padding: 1rem 1.5rem 1.25rem; border-bottom: 1px solid #e5e7eb; }
        .asset-settings-field-label {
          /* Matches .modern-label, which every other settings form uses. */
          font-size: 0.8125rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.375rem;
        }
        .asset-settings-field-hint { font-size: 0.75rem; color: #6b7280; margin-top: 4px; }
      `}</style>
    </div>
  );
};

export default AssetSettings;
